import {
  useCallback, useEffect, useRef, useState
} from 'react';
import ConversationModelMC from '../../../models/inbox/ConversationModelMC';

const PAGE_SIZE = 5;

/**
 * Carga el historial paginado de scores (Timeline) de un cliente/plataforma.
 * Paginación por cursor `before` (ISO date del registro más antiguo cargado),
 * siguiendo el contrato real del endpoint `score-history` del microservicio AI Copilots.
 */
const useLeadScoreHistoryData = ({ customerId, platform }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);

  const requestIdRef = useRef(0);
  const lastBeforeRef = useRef(undefined);
  const loadingRef = useRef(false);
  const seenIdsRef = useRef(new Set());

  const loadPage = useCallback(async (before) => {
    if (!customerId || !platform) return;

    lastBeforeRef.current = before;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const { history: page, error: hasError } = await ConversationModelMC.getLeadScoreHistory({
        customerId, platform, limit: PAGE_SIZE, before
      });
      if (requestId !== requestIdRef.current) return; // respuesta de un request ya superado, se descarta

      if (hasError) {
        setError(true);
        return;
      }

      const items = page || [];

      if (!before) {
        seenIdsRef.current = new Set(items.map((item) => item.id || item.message_id));
        setHistory(items);
        setHasMore(items.length === PAGE_SIZE);
        return;
      }

      // El cursor es el created_at del último item cargado: si el microservicio lo tratara
      // como inclusivo, o dos eventos comparten timestamp, la página siguiente podría repetir
      // el borde. Se descarta por id/message_id antes de concatenar, y si la página filtrada
      // no aporta nada nuevo se corta hasMore para no reintentar el mismo cursor en loop.
      const newItems = items.filter((item) => !seenIdsRef.current.has(item.id || item.message_id));
      newItems.forEach((item) => seenIdsRef.current.add(item.id || item.message_id));
      setHistory((prev) => [...prev, ...newItems]);
      setHasMore(items.length === PAGE_SIZE && newItems.length > 0);
    } catch {
      if (requestId === requestIdRef.current) setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [customerId, platform]);

  useEffect(() => {
    seenIdsRef.current = new Set();
    setHistory([]);
    setHasMore(false);
    setError(false);
    loadPage();
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current || history.length === 0) return;
    loadingRef.current = true;
    loadPage(history[history.length - 1].created_at).finally(() => {
      loadingRef.current = false;
    });
  }, [hasMore, history, loadPage]);

  const retry = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    loadPage(lastBeforeRef.current).finally(() => {
      loadingRef.current = false;
    });
  }, [loadPage]);

  return {
    history, loading, hasMore, loadMore, error, retry
  };
};

export default useLeadScoreHistoryData;
